// src/pages/Contact.tsx
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Instagram, Facebook, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    ime: '',
    email: '',
    zadeva: '',
    sporocilo: '',
    gdprConsent: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Hero Section */}
        <section className="text-center mb-12">
          <div className="max-w-4xl mx-auto">
            <h1 
              className="text-5xl md:text-7xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '0.5rem'
              }}
            >
              Kontakt
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Stopite v stik z mano in začnimo skupaj ustvarjati nekaj posebnega
            </p>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="glass-card p-8 rounded-3xl">
            <h2 
              className="text-3xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Pošljite sporočilo
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="ime" className="block text-white/90 font-medium mb-2">
                  Ime in priimek *
                </label>
                <input
                  type="text"
                  id="ime"
                  name="ime"
                  value={formData.ime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-hidden focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  placeholder="Vnesite vaše ime"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white/90 font-medium mb-2">
                  E-naslov *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-hidden focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  placeholder="vas.email@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="zadeva" className="block text-white/90 font-medium mb-2">
                  Zadeva *
                </label>
                <input
                  type="text"
                  id="zadeva"
                  name="zadeva"
                  value={formData.zadeva}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-hidden focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all"
                  placeholder="O čem bi se radi pogovorili?"
                  required
                />
              </div>

              <div>
                <label htmlFor="sporocilo" className="block text-white/90 font-medium mb-2">
                  Sporočilo *
                </label>
                <textarea
                  id="sporocilo"
                  name="sporocilo"
                  value={formData.sporocilo}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-hidden focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-all resize-none"
                  placeholder="Opišite vaše potrebe, ideje ali vprašanja..."
                  required
                />
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  name="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-purple-400 bg-white/10 border-white/20 rounded focus:ring-purple-400 focus:ring-2 mt-0.5"
                  required
                />
                <label htmlFor="gdprConsent" className="text-white/80 text-sm leading-relaxed">
                  Strinjam se z obdelavo osebnih podatkov v skladu s{' '}
                  <a href="/pravni-dokumenti/politika-zasebnosti" className="text-purple-300 hover:text-purple-200 underline">
                    politiko zasebnosti
                  </a>
                  . *
                </label>
              </div>

              <button
                type="submit"
                className="w-full glass-card-hover px-8 py-4 text-lg font-medium text-white inline-flex items-center justify-center space-x-2 group"
              >
                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                <span>Pošlji sporočilo</span>
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="glass-card p-8 rounded-3xl">
              <h2 
                className="text-3xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Kako me najdete
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">E-pošta</h3>
                    <a 
                      href="mailto:alma.lovrencic@gmail.com"
                      className="text-purple-300 hover:text-purple-200 transition-colors"
                    >
                      alma.lovrencic@gmail.com
                    </a>
                    <p className="text-white/60 text-sm mt-1">
                      Odgovorim v 24 urah
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Telefon</h3>
                    <a 
                      href="tel:+38651479667"
                      className="text-purple-300 hover:text-purple-200 transition-colors"
                    >
                      +386 51 479 667
                    </a>
                    <p className="text-white/60 text-sm mt-1">
                      Pon-Pet: 9:00-17:00
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Atelje</h3>
                    <address className="text-white/80 not-italic">
                      Žički prehod 4<br />
                      2000 Maribor<br />
                      Slovenija
                    </address>
                    <p className="text-white/60 text-sm mt-1">
                      Obisk po dogovoru
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-3xl">
              <h3 
                className="text-2xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Delovni čas
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="text-white/90">Ponedeljek - Petek</span>
                      <span className="text-white/70">9:00 - 17:00</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="text-white/90">Sobota</span>
                      <span className="text-white/70">10:00 - 15:00</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-purple-400" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <span className="text-white/90">Nedelja</span>
                      <span className="text-white/70">Zaprto</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-purple-400/10 rounded-xl">
                <p className="text-white/80 text-sm leading-relaxed">
                  <strong>Opomba:</strong> Za obisk ateljeja se prosim predhodno dogovorite. 
                  Delavnice potekajo tudi izven rednega delovnega časa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;